/**
 * Estado da aba de impersonate do suporte ("entrar como usuário").
 *
 * O admin abre uma NOVA aba em /impersonate#imp=<payload>. Esta aba guarda a
 * sessão de suporte só em sessionStorage (por aba) e nunca toca no
 * localStorage, onde fica a sessão do admin compartilhada entre abas.
 *
 * Funções puras sobre um Storage/Location injetado, para teste sem DOM.
 */

export const IMPERSONATION_STORAGE_KEY = 'cai_impersonation';
export const IMPERSONATION_HASH_PARAM = 'imp';
export const IMPERSONATION_PATH = '/impersonate';

export interface ImpersonationState {
  token: string;
  sessionId: string;
  expiresAt: string;
  target: { id: string; email: string | null; displayName: string | null };
}

type MinimalStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function isState(value: unknown): value is ImpersonationState {
  const v = value as ImpersonationState;
  return !!v &&
    typeof v.token === 'string' && v.token.startsWith('imp_') &&
    typeof v.sessionId === 'string' &&
    typeof v.expiresAt === 'string' && !Number.isNaN(Date.parse(v.expiresAt)) &&
    !!v.target && typeof v.target.id === 'string';
}

export function isImpersonationExpired(state: ImpersonationState, now: number = Date.now()): boolean {
  return Date.parse(state.expiresAt) <= now;
}

/** base64url(JSON) — vai no fragmento da URL, que não chega a servidor/proxy nem ao Referer. */
export function encodeImpersonationPayload(state: ImpersonationState): string {
  const bytes = new TextEncoder().encode(JSON.stringify(state));
  let binary = '';
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function decodeImpersonationPayload(payload: string): ImpersonationState | null {
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const parsed = JSON.parse(new TextDecoder().decode(bytes));
    return isState(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function buildImpersonationUrl(origin: string, state: ImpersonationState): string {
  return `${origin}${IMPERSONATION_PATH}#${IMPERSONATION_HASH_PARAM}=${encodeImpersonationPayload(state)}`;
}

/** Payload no hash (`#imp=...`), ou null. */
export function readImpersonationFromHash(hash: string): ImpersonationState | null {
  if (!hash || hash.length < 2) return null;
  const payload = new URLSearchParams(hash.substring(1)).get(IMPERSONATION_HASH_PARAM);
  return payload ? decodeImpersonationPayload(payload) : null;
}

export function loadImpersonation(storage: MinimalStorage, now: number = Date.now()): ImpersonationState | null {
  try {
    const raw = storage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isState(parsed) || isImpersonationExpired(parsed, now)) {
      storage.removeItem(IMPERSONATION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function saveImpersonation(storage: MinimalStorage, state: ImpersonationState): void {
  try {
    storage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage indisponível: a sessão vale só enquanto a página estiver aberta.
  }
}

export function clearImpersonation(storage: MinimalStorage): void {
  try {
    storage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Ao carregar a aba: payload no hash tem prioridade (e é salvo + removido da
 * URL); senão, o que já estava no sessionStorage desta aba.
 */
export function bootstrapImpersonation(options: {
  hash: string;
  storage: MinimalStorage;
  clearHash: () => void;
  now?: number;
}): ImpersonationState | null {
  const fromHash = readImpersonationFromHash(options.hash);
  if (fromHash) {
    options.clearHash();
    if (isImpersonationExpired(fromHash, options.now)) return null;
    saveImpersonation(options.storage, fromHash);
    return fromHash;
  }
  return loadImpersonation(options.storage, options.now);
}

/** mm:ss restante (00:00 quando venceu). */
export function formatRemaining(expiresAt: string, now: number = Date.now()): string {
  const totalSec = Math.max(0, Math.floor((Date.parse(expiresAt) - now) / 1000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export const SUPPORT_MODE_BLOCKED_MESSAGE =
  'Indisponível em modo suporte: cobrança, assinatura, senha e e-mail do cliente não podem ser alterados pelo suporte.';

/** Esta aba é uma sessão de suporte? (lê o sessionStorage da aba) */
export function isSupportTab(): boolean {
  try {
    return typeof window !== 'undefined' && loadImpersonation(window.sessionStorage) !== null;
  } catch {
    return false;
  }
}

/**
 * Guard de UI para ações bloqueadas no modo suporte. O checkout OnProfit é um
 * link externo (window.open), então só dá para bloquear aqui; o resto a API
 * também recusa (403 impersonation_forbidden).
 */
export function blockInSupportMode(): boolean {
  if (!isSupportTab()) return false;
  window.alert(SUPPORT_MODE_BLOCKED_MESSAGE);
  return true;
}
